import React, { useEffect, useMemo, useState } from "react"
import { Card, CardBody, CardTitle, Button } from "reactstrap"
import { Formik, Form, Field } from "lib/formik"
import Select from "components/Shared/TranslatedSelect"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"

import Breadcrumbs from "components/Common/Breadcrumb"
import BackButton from "components/Shared/BackButton"
import TextError from "components/Shared/TextError"

import { getCountryList } from "store/actions/countriesAction"

import {
  addMenuSetup,
  getMenuSetup,
  updateMenu,
} from "store/actions/menuSetupAction"
import { showAlert } from "store/reducers/alertReducer"
import { useNavigate, useLocation } from "react-router-dom"
import Switch from "components/Shared/Switch"
import { addMenuSetupSchemas } from "components/Common/validation"
import { useBrandOptions } from "hooks"
import { getSubPermissions } from "common/isPermissionEnabled"

const initialValues = {
  menuId: "",
  appMenuId: "",
  menuNameEn: "",
  menuNameAr: "",
  menuDescriptionAr: "",
  menuDescriptionEn: "",
  brand: null,
  countryCode: [], // now an array
  status: false,
  isDefaultMenu: false,
}

const AddMenuSetup = ({ mode }) => {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { allowedBrands } = useBrandOptions()

  const { countryList } = useSelector(state => state.Countries)
  const { singleMenuSetup } = useSelector(state => state.MenuSetup)
  const { userRole, accountType } = useSelector(state => state.Login)

  const [formValues, setFormValues] = useState(initialValues)
  const [isLinkWithPos, setIsLinkWithPos] = useState(false)

  const menuPermissionId = mode === "add" ? "menus_2" : "menus_3"

  const canLinkWithPos = useMemo(() => {
    const menuSubs =
      getSubPermissions(userRole, 4, menuPermissionId, accountType) || {}
    return menuSubs.link_menu_with_pos?.isEnabled === true
  }, [userRole, accountType, menuPermissionId])

  const queryParams = new URLSearchParams(location.search)
  const menuId = queryParams.get("id")

  useEffect(() => {
    dispatch(getCountryList())
    if (mode === "edit" && menuId) {
      dispatch(getMenuSetup(menuId))
    }
  }, [mode])

  useEffect(() => {
    if (singleMenuSetup && mode === "edit") {
      const hasPosLink = Boolean(
        singleMenuSetup.appMenuId || singleMenuSetup.menuId
      )
      setIsLinkWithPos(canLinkWithPos ? hasPosLink : false)
      setFormValues({
        menuId: singleMenuSetup.menuId || "",
        appMenuId: singleMenuSetup.appMenuId || "",
        menuNameEn: singleMenuSetup.menuNameEn || "",
        menuNameAr: singleMenuSetup.menuNameAr || "",
        menuDescriptionAr: singleMenuSetup.menuDescriptionAr || "",
        menuDescriptionEn: singleMenuSetup.menuDescriptionEn || "",
        brand: singleMenuSetup.brand || null,
        countryCode: Array.isArray(singleMenuSetup.countryCode)
          ? singleMenuSetup.countryCode
          : [singleMenuSetup.countryCode].filter(Boolean),
        status: singleMenuSetup.status === 1,
        isDefaultMenu: singleMenuSetup.isDefaultMenu || false,
      })
    }
  }, [singleMenuSetup, canLinkWithPos, mode])

  const countryOptions = countryList?.map(item => ({
    label: i18n.language === "sa" ? item.nameAr : item.name,
    value: item.countryCode,
  }))

  const brandOptions =
    allowedBrands?.map(obj => ({
      value: obj._id,
      label: obj.brandName,
    })) || []

  const handleSubmit = values => {
    const finalValues = {
      ...values,
      status: values.status ? 1 : 0,
      appMenuId:
        canLinkWithPos && isLinkWithPos
          ? values.appMenuId || ""
          : mode === "edit" && singleMenuSetup?.appMenuId
          ? singleMenuSetup.appMenuId
          : "",
      menuId:
        canLinkWithPos && isLinkWithPos
          ? values.menuId || ""
          : mode === "edit" && singleMenuSetup?.menuId
          ? singleMenuSetup.menuId
          : "",
    }

    if (mode === "edit") {
      dispatch(updateMenu({ ...finalValues, id: menuId }))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate(-1)
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(err => console.log(err))
      return
    }

    dispatch(addMenuSetup(finalValues))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          navigate(-1)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Card>
          <CardBody>
            <CardTitle className="d-flex align-items-center">
              <BackButton />
              {mode === "edit" ? t("Edit") : t("Add")}
            </CardTitle>
            <Formik
              initialValues={formValues}
              onSubmit={handleSubmit}
              enableReinitialize={true}
              validationSchema={addMenuSetupSchemas({
                requirePosFields: canLinkWithPos && isLinkWithPos,
              })}
            >
              {({ errors, touched, values, setFieldValue }) => {
                const handleLinkWithPosToggle = () => {
                  const nextValue = !isLinkWithPos
                  setIsLinkWithPos(nextValue)
                  if (!nextValue) {
                    setFieldValue("appMenuId", "")
                    setFieldValue("menuId", "")
                  } else if (mode === "edit" && singleMenuSetup) {
                    setFieldValue("appMenuId", singleMenuSetup.appMenuId || "")
                    setFieldValue("menuId", singleMenuSetup.menuId || "")
                  }
                }

                return (
                  <Form>
                    <div className="row">
                      <div className="col-lg-6 mt-3">
                        <label>{t("Select Country(s)")}</label>
                        <Select
                          isMulti
                          value={countryOptions.filter(option =>
                            values.countryCode.includes(option.value)
                          )}
                          onChange={selected => {
                            const selectedValues = selected.map(
                              item => item.value
                            )
                            setFieldValue("countryCode", selectedValues)
                          }}
                          options={countryOptions}
                          placeholder={t("Select Country")}
                          className={`select2-selection ${
                            touched.countryCode && errors.countryCode
                              ? "danger_color_border"
                              : ""
                          }`}
                        />
                        <TextError name="countryCode" />
                      </div>
                      <div className="col-lg-6 mt-3">
                        <label>{t("Select Brand")}</label>
                        <Select
                          value={brandOptions.find(
                            option => option.value === values.brand
                          )}
                          onChange={selected =>
                            setFieldValue("brand", selected?.value)
                          }
                          options={brandOptions}
                          placeholder={t("Select Brand")}
                          className={`select2-selection ${
                            touched.brand && errors.brand
                              ? "danger_color_border"
                              : ""
                          }`}
                        />
                        <TextError name="brand" />
                      </div>
                      {canLinkWithPos && (
                        <div className="col-lg-6">
                          <div className="form_field d-flex align-items-center mt-4">
                            <label className="mb-0 me-2">
                              {t("Link with POS")}
                            </label>
                            <Switch
                              isChecked={isLinkWithPos}
                              onClickHandler={handleLinkWithPosToggle}
                            />
                          </div>
                          <small className="text-muted d-block">
                            {t(
                              "Enable to link menu with Syrve POS terminal"
                            )}
                          </small>
                        </div>
                      )}
                      {canLinkWithPos && isLinkWithPos && (
                        <>
                          <div className="col-lg-6">
                            <div className="form_field">
                              <label htmlFor="appMenuId">
                                <span className="text-danger">*</span>{" "}
                                {t("App Menu Id")}
                              </label>
                              <Field
                                type="text"
                                name="appMenuId"
                                placeholder={t("App Menu Id")}
                                className={`form-control ${
                                  touched.appMenuId && errors.appMenuId
                                    ? "is-invalid"
                                    : ""
                                }`}
                              />
                              <TextError name="appMenuId" />
                            </div>
                          </div>
                          <div className="col-lg-6">
                            <div className="form_field">
                              <label htmlFor="menuId">{t("POS ID")}</label>
                              <Field
                                type="text"
                                name="menuId"
                                placeholder={t("POS ID")}
                                className={`form-control ${
                                  touched.menuId && errors.menuId
                                    ? "is-invalid"
                                    : ""
                                }`}
                              />
                              <TextError name="menuId" />
                            </div>
                          </div>
                        </>
                      )}

                      <div className="col-lg-6">
                        <div className="form_field">
                          <label htmlFor="menuNameEn">
                            <span className="text-danger">*</span>{" "}
                            {t("Menu Name (English)")}
                          </label>
                          <Field
                            type="text"
                            name="menuNameEn"
                            placeholder={t("Menu Name (English)")}
                            className={`form-control ${
                              touched.menuNameEn && errors.menuNameEn
                                ? "is-invalid"
                                : ""
                            }`}
                          />
                          <TextError name="menuNameEn" />
                        </div>
                      </div>

                      <div className="col-lg-6">
                        <div className="form_field">
                          <label htmlFor="menuNameAr">
                            <span className="text-danger">*</span>{" "}
                            {t("Menu Name (عربي)")}
                          </label>
                          <Field
                            style={{ direction: "rtl" }}
                            type="text"
                            name="menuNameAr"
                            placeholder={t("Menu Name (عربي)")}
                            className={`form-control ${
                              touched.menuNameAr && errors.menuNameAr
                                ? "is-invalid"
                                : ""
                            }`}
                          />
                          <TextError name="menuNameAr" />
                        </div>
                      </div>

                      <div className="col-lg-6 mt-3">
                        <label>{t("Description (English)")}</label>
                        <Field
                          as="textarea"
                          name="menuDescriptionEn"
                          placeholder={t("Description (English)")}
                          className={`form-control ${
                            touched.menuDescriptionEn &&
                            errors.menuDescriptionEn
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <TextError name="menuDescriptionEn" />
                      </div>

                      <div className="col-lg-6 mt-3">
                        <label>{t("Description (عربي)")}</label>
                        <Field
                          as="textarea"
                          name="menuDescriptionAr"
                          placeholder={t("Description (عربي)")}
                          style={{ direction: "rtl" }}
                          className={`form-control ${
                            touched.menuDescriptionAr &&
                            errors.menuDescriptionAr
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <TextError name="menuDescriptionAr" />
                      </div>

                      <div className="row mt-3">
                        <div className="col-lg-6 d-flex align-items-center">
                          <label className="mb-0 me-2">
                            {t("Active/Inactive")}
                          </label>
                          <Field name="status">
                            {({ field, form }) => (
                              <Switch
                                isChecked={field.value}
                                onClickHandler={() =>
                                  form.setFieldValue("status", !field.value)
                                }
                              />
                            )}
                          </Field>
                        </div>

                        <div className="col-lg-6 d-flex align-items-center">
                          <label className="mb-0 me-2">
                            {t("Default Menu")}
                          </label>
                          <Field name="isDefaultMenu">
                            {({ field, form }) => (
                              <Switch
                                isChecked={field.value}
                                onClickHandler={() =>
                                  form.setFieldValue(
                                    "isDefaultMenu",
                                    !field.value
                                  )
                                }
                              />
                            )}
                          </Field>
                        </div>
                      </div>

                      <div className="col-12 d-flex justify-content-end my-4">
                        <Button
                          type="button"
                          className="mx-2"
                          color="secondary"
                          onClick={() => window.history.back()}
                        >
                          {t("Cancel")}
                        </Button>
                        <Button type="submit" color="success">
                          {mode === "edit" ? t("Save") : t("Add")}
                        </Button>
                      </div>
                    </div>
                  </Form>
                )
              }}
            </Formik>
          </CardBody>
        </Card>
      </div>
    </div>
  )
}

export default AddMenuSetup
